import { gql } from '@apollo/client';
import { ACH_TRANSFER, ACH_TRANSFER_EDGE } from './achTransfers';
import { TRADE_EDGE_FRAGMENT, TRADE_FRAGMENT } from './trades';
import {
  DIVIDEND_DISTRIBUTION_EDGE,
  DIVIDEND_DISTRIBUTION_FRAGMENT,
  CAPITAL_GAINS_DISTRIBUTION_FRAGMENT,
  RETURN_OF_CAPITAL_DISTRIBUTION_FRAGMENT,
} from './dividends';
import { INTEREST_DISTRIBUTION_FRAGMENT } from './interestDistribution';
import { ICT_TRANSFERS_FRAGMENT } from './IctTransfers';
import { CHECK_TRANSFERS_FRAGMENT } from './checkTransfers';

export const GET_ACH_TRANSFER_HISTORY = gql`
  query GetAchTransferHistory(
    $first: Int
    $after: Cursor
    $portfolioId: ID
    $achType: [ACHTransferTransferType!]
    $startDate: Time
    $endDate: Time
  ) {
    pages: achTransfers(
      where: {
        hasPortfolioWith: { id: $portfolioId }
        typeIn: $achType
        createTimeGT: $startDate
        createTimeLT: $endDate
      }
      first: $first
      after: $after
      orderBy: { direction: DESC, field: create_time }
    ) {
      edges {
        ...AchTransferEdge
      }
      pageInfo {
        startCursor
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
  ${ACH_TRANSFER_EDGE}
`;

export const GET_TRADE_HISTORY = gql`
  query GetTradeHistory(
    $first: Int
    $after: Cursor
    $today: Time!
    $portfolioId: ID
    $tradeType: [TradeMarketSide!]
    $startDate: Time
    $endDate: Time
    $allFilters: [TradeWhereInput!]
  ) {
    pages: trades(
      first: $first
      after: $after
      where: {
        and: $allFilters
        or: [{ statusNEQ: TRADE_STATUS_FAILED }, { createTimeGTE: $today }]
        hasPortfolioWith: { id: $portfolioId }
        sideIn: $tradeType
        createTimeGT: $startDate
        createTimeLT: $endDate
      }
      orderBy: { direction: DESC, field: create_time }
    ) {
      edges {
        ...TradeEdgeFragment
      }
      pageInfo {
        startCursor
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
  ${TRADE_EDGE_FRAGMENT}
`;

export const GET_DIVIDEND_HISTORY = gql`
  query GetDividendHistory($first: Int, $after: Cursor) {
    pages: dividendDistributions(first: $first, after: $after) {
      edges {
        ...DividendDistributionEdge
      }
      pageInfo {
        startCursor
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
  ${DIVIDEND_DISTRIBUTION_EDGE}
`;

export const BOND_MATURITY_DISTRIBUTION_FRAGMENT = gql`
  fragment BondMaturityDistributionFragment on BondMaturityDistribution {
    id
    amount
    value
    cashRate
    orderStatus
    createTime
    details: bondMaturity {
      totalValue
    }
    sourceAsset {
      name
      symbol
      cusip
      details {
        ... on SecurityFixedIncomeDetails {
          couponRate
          maturityDate
        }
      }
    }
    distributionAsset {
      symbol
    }
  }
`;

export const BOND_CALLED_DISTRIBUTION_FRAGMENT = gql`
  fragment BondCalledDistributionFragment on BondCalledDistribution {
    id
    amount
    value
    cashRate
    orderStatus
    createTime
    details: bondCalled {
      totalValue
    }
    sourceAsset {
      name
      symbol
      cusip
      details {
        ... on SecurityFixedIncomeDetails {
          couponRate
          maturityDate
        }
      }
    }
    distributionAsset {
      symbol
    }
  }
`;

export const ACTIVITY_HISTORY_JOURNAL_TRANSFER_ORDER_FRAGMENT = gql`
  fragment ActivityHistoryJournalTransfer on JournalTransferOrder {
    __typename
    id
    journalTransferType: type
    amount
    instantDepositAmount
    returnFee
    isReturned
    retirementContributionInfo {
      type
    }
    orderType
    orderStatus
    createTime
  }
`;

export const ACTIVITY_HISTORY_GENERIC_TRANSFER_ORDER_FRAGMENT = gql`
  fragment ActivityHistoryGenericTransfer on GenericTransferOrder {
    __typename
    id
    genericTransferType: type
    amount
    instantDepositAmount
    returnFee
    isReturned
    retirementContributionInfo {
      type
    }
    orderType
    orderStatus
    createTime
  }
`;

export const GET_ALL_TRANSACTION_HISTORY = gql`
  query GetAllHistory(
    $first: Int
    $after: Cursor
    $portfolioId: ID
    $orderType: [OrderOrderType!]
    $startDate: Time
    $endDate: Time
  ) {
    pages: orders(
      first: $first
      after: $after
      orderBy: { direction: DESC, field: create_time }
      where: {
        typeIn: $orderType
        hasPortfolioWith: { id: $portfolioId }
        createTimeGT: $startDate
        createTimeLT: $endDate
      }
    ) {
      edges {
        node {
          id
          __typename
          ... on Trade {
            ...TradeFragment
          }
          ... on ACHTransfer {
            ...AchTransfer
          }
          ... on ICTTransfer {
            ...IctTransfer
          }
          ... on CheckTransfer {
            ...CheckTransfer
          }
          ... on InterestDistribution {
            ...InterestDistributionFragment
          }
          ... on DividendDistribution {
            ...DividendDistributionFragment
          }
          ... on CapitalGainsDistribution {
            ...CapitalGainsDistributionFragment
          }
          ... on ReturnOfCapitalDistribution {
            ...ReturnOfCapitalDistributionFragment
          }
          ... on BondMaturityDistribution {
            ...BondMaturityDistributionFragment
          }
          ... on BondCalledDistribution {
            ...BondCalledDistributionFragment
          }
          ... on JournalTransferOrder {
            ...ActivityHistoryJournalTransfer
          }
          ... on GenericTransferOrder {
            ...ActivityHistoryGenericTransfer
          }
        }
      }
      pageInfo {
        startCursor
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
  ${TRADE_FRAGMENT}
  ${ACH_TRANSFER}
  ${ICT_TRANSFERS_FRAGMENT}
  ${CHECK_TRANSFERS_FRAGMENT}
  ${ACTIVITY_HISTORY_JOURNAL_TRANSFER_ORDER_FRAGMENT}
  ${ACTIVITY_HISTORY_GENERIC_TRANSFER_ORDER_FRAGMENT}
  ${INTEREST_DISTRIBUTION_FRAGMENT}
  ${DIVIDEND_DISTRIBUTION_FRAGMENT}
  ${CAPITAL_GAINS_DISTRIBUTION_FRAGMENT}
  ${RETURN_OF_CAPITAL_DISTRIBUTION_FRAGMENT}
  ${BOND_MATURITY_DISTRIBUTION_FRAGMENT}
  ${BOND_CALLED_DISTRIBUTION_FRAGMENT}
`;
